// Change password as a centered two-step dialog (owner decision: settings
// dialogs are popups, not bottom sheets, and the flow is stepped like a
// modern account page): step 1 proves the CURRENT password against the
// server, step 2 takes the new one twice. On success the server emails a
// security notice to the account address.
import { useState } from "react";
import { api, ApiError } from "../lib/api";
import { invalidateMe } from "../lib/useMe";
import { useT, useLocale, apiLang } from "../lib/i18n";
import { useToast } from "../lib/toast";
import { SheetShell } from "./SheetShell";
import { Button } from "./ui/Button";

export function ChangePasswordSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useT();
  const { locale } = useLocale();
  const toast = useToast();
  const [step, setStep] = useState<1 | 2>(1);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function reset() {
    setStep(1); setCurrent(""); setNext(""); setConfirm(""); setErr(null); setBusy(false);
  }

  async function verifyCurrent(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await api.verifyPassword(current);
      setStep(2);
    } catch (e) {
      setErr(e instanceof ApiError && e.message === "wrong_password"
        ? t("auth.wrongPassword") : t("auth.genericError"));
    } finally {
      setBusy(false);
    }
  }

  async function submitNew(e: React.FormEvent) {
    e.preventDefault();
    if (next !== confirm) { setErr(t("auth.password_mismatch")); return; }
    setErr(null);
    setBusy(true);
    try {
      await api.changePassword(current, next, apiLang(locale));
      invalidateMe();
      toast.success(t("toast.saved"));
      reset();
      onClose();
    } catch (e) {
      setErr(e instanceof ApiError && e.message === "weak_password"
        ? t("auth.weakPassword")
        : e instanceof ApiError && e.message === "wrong_password"
          ? t("auth.wrongPassword")
          : t("auth.genericError"));
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;

  return (
    <SheetShell variant="center" onClose={() => { reset(); onClose(); }}>
      <div className="space-y-5 pt-1">
        <h2 className="text-lg font-semibold">{t("auth.changePassword")}</h2>

        {step === 1 ? (
          <form onSubmit={verifyCurrent} className="space-y-3">
            {/* No `required` — social-only accounts with no password yet may
                leave this blank; the backend accepts it when none is set. */}
            <input
              className="input"
              type="password"
              placeholder={t("auth.currentPassword")}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              autoFocus
            />
            {err && <p className="text-sm text-danger">{err}</p>}
            <Button type="submit" disabled={busy} className="w-full">
              {t("common.continue")}
            </Button>
          </form>
        ) : (
          <form onSubmit={submitNew} className="space-y-3">
            <input
              className="input"
              type="password"
              required
              minLength={8}
              placeholder={t("auth.newPassword")}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              autoFocus
            />
            <input
              className="input"
              type="password"
              required
              minLength={8}
              placeholder={t("auth.confirmPassword")}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
            />
            <p className="text-sm text-fg-muted">{t("auth.change_notice")}</p>
            {err && <p className="text-sm text-danger">{err}</p>}
            <Button type="submit" disabled={busy} className="w-full">
              {t("auth.changePassword")}
            </Button>
            <button
              type="button"
              onClick={() => { setStep(1); setErr(null); }}
              className="w-full py-2 text-sm text-fg-muted press"
            >
              {t("common.back")}
            </button>
          </form>
        )}
      </div>
    </SheetShell>
  );
}
